import { useState, useMemo, useCallback } from 'react';
import { LessonService, LessonPayload } from '../../../services/lessons/lesson.service';
import { Interaction } from '../types/lesson.types';

export const useLessonData = (locationState: any, topicId?: number, levelId?: number, subTopicId?: number) => {
  const [lessonData, setLessonData] = useState<LessonPayload | null>(locationState?.lessonData || null);
  
  const [names, setNames] = useState({
    levelName: locationState?.levelName || '',
    topicName: locationState?.topicName || '',
    subTopicName: locationState?.subTopicName || ''
  });

  // Flatten logic
  const interactions = useMemo<Interaction[]>(() => {
    if (!lessonData) return [];
    const items: Interaction[] = [];
    if (lessonData.lesson_video_url) {
      items.push({ id: 'video', type: 'video', url: lessonData.lesson_video_url, phaseIndex: 1 });
    }
    let currentPhaseIdx = 1;
    lessonData.content.forEach((step: any, sIdx: number) => {
      const stepItems: any[] = [];
      stepItems.push({
        id: `step_${sIdx}_content`,
        type: 'content',
        content: step.teacher_content,
        title: step.title,
        stepNumber: sIdx + 1,
        phaseIndex: currentPhaseIdx,
        chart: step.chart_url,
        video: step.video_url
      });
      if (step.quiz && step.quiz.question) {
        stepItems.push({ 
          id: `step_${sIdx}_quiz`, 
          type: 'quiz', 
          quiz: step.quiz, 
          title: step.title, 
          stepNumber: sIdx + 1,
          phaseIndex: currentPhaseIdx 
        });
      }
      if (step.teacher_summary) {
        stepItems.push({ 
          id: `step_${sIdx}_summary`, 
          type: 'summary', 
          content: step.teacher_summary, 
          title: step.title, 
          stepNumber: sIdx + 1,
          phaseIndex: currentPhaseIdx 
        });
      }

      const isPhaseStop = lessonData.phases?.some((p: any) => p.step_num === sIdx + 1);
      if (isPhaseStop && stepItems.length > 0) {
        stepItems[stepItems.length - 1].isPhaseStop = true;
      }

      items.push(...stepItems);
      if (isPhaseStop) currentPhaseIdx++;
    });
    return items;
  }, [lessonData]);

  const fetchLesson = useCallback(async () => {
    const lessonService = new LessonService();
    const { lessonId: stateLessonId } = locationState || {};
    
    if (lessonData && (stateLessonId === lessonData.id || (!stateLessonId && topicId === lessonData.topic_id))) {
      return {
        data: lessonData,
        lessonId: lessonData.id,
        effectiveTopicId: lessonData.topic_id,
        effectiveLevelId: lessonData.level_id,
        effectiveSubTopicId: lessonData.sub_topic_id
      };
    }

    const data = stateLessonId
      ? await lessonService.getLesson(stateLessonId)
      : await lessonService.getLessonByTopic(topicId!, levelId!, subTopicId);
      
    if (!data) throw new Error("Lesson not found");
    
    setLessonData(data);
    
    return {
      data,
      lessonId: data.id,
      effectiveTopicId: topicId || data.topic_id,
      effectiveLevelId: levelId || data.level_id,
      effectiveSubTopicId: subTopicId !== undefined ? subTopicId : data.sub_topic_id
    };
  }, [locationState, topicId, levelId, subTopicId]);

  const resolveNames = useCallback(async (effectiveLevelId: number, effectiveTopicId: number, effectiveSubTopicId?: number) => {
    if (names.levelName && names.topicName) return;

    try {
      const lessonService = new LessonService();
      const [levels, topics] = await Promise.all([
        lessonService.getLessonLevels(),
        lessonService.getLessonTopics(effectiveLevelId)
      ]);

      const lName = levels.find(l => l.id === effectiveLevelId)?.name || '';
      const tName = topics.find(t => t.id === effectiveTopicId)?.name || '';
      let stName = '';

      if (effectiveSubTopicId && effectiveSubTopicId !== -1) {
        const subTopics = await lessonService.getLessonSubTopics(effectiveLevelId, effectiveTopicId);
        stName = subTopics.find(s => s.id === effectiveSubTopicId)?.name || '';
      }

      setNames(prev => ({ 
        levelName: prev.levelName || lName, 
        topicName: prev.topicName || tName, 
        subTopicName: prev.subTopicName || stName 
      }));
    } catch (e) {
      console.error("Failed to fetch name fallbacks", e);
    }
  }, [names.levelName, names.topicName]);

  return {
    lessonData,
    names,
    setNames,
    interactions,
    fetchLesson,
    resolveNames
  };
};
// Agent synced
